/**
 * Feature Flags Service - Firebase Remote Config
 *
 * This module provides feature flag management using Firebase Remote Config.
 * It supports:
 * - Boolean flags (enabled/disabled)
 * - String/numeric values
 * - Real-time updates
 * - Local caching
 * - Fallback values
 */

import { useEffect, useState } from 'react';
import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
  getBoolean,
  RemoteConfig
} from 'firebase/remote-config';
import app from '../services/firebase';

// Initialize Remote Config
const remoteConfig: RemoteConfig = getRemoteConfig(app);

// Configuration settings
remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour in production
remoteConfig.settings.fetchTimeoutMillis = 10000; // 10 seconds

// Default values (fallback)
remoteConfig.defaultConfig = {
  // Frontend feature flags
  'genui_adaptive_layout': false,
  'genui_dark_mode': false,
  'genui_high_contrast': false,
  'genui_llm_provider': 'gemini',

  // UI Experiments
  'new_admin_card_enabled': true,  // Redesigned admin card with 🔐 icon
  'advanced_filters_enabled': false,
  'beta_dashboard_enabled': false,

  // Backend feature flags (for transparency/debugging)
  'llm_fallback_enabled': true,
  'semantic_cache_enabled': true,
  'retry_with_backoff': true,
  'tokenization_v2': false,
};

/**
 * Feature flag constants
 * Use these constants to reference flags throughout the application
 */
export const FEATURE_FLAGS = {
  // Frontend flags
  ADAPTIVE_LAYOUT: 'genui_adaptive_layout',
  DARK_MODE: 'genui_dark_mode',
  HIGH_CONTRAST: 'genui_high_contrast',
  LLM_PROVIDER: 'genui_llm_provider',

  // UI Experiments
  NEW_ADMIN_CARD: 'new_admin_card_enabled',
  ADVANCED_FILTERS: 'advanced_filters_enabled',
  BETA_DASHBOARD: 'beta_dashboard_enabled',

  // Backend flags
  LLM_FALLBACK: 'llm_fallback_enabled',
  SEMANTIC_CACHE: 'semantic_cache_enabled',
  RETRY_BACKOFF: 'retry_with_backoff',
  TOKENIZATION_V2: 'tokenization_v2',
} as const;

/**
 * Initialize Remote Config
 * Should be called once during application startup
 */
export async function initializeRemoteConfig(): Promise<void> {
  try {
    const activated = await fetchAndActivate(remoteConfig);
    if (activated) {
      console.log('Firebase Remote Config: New config fetched and activated');
    } else {
      console.log('Firebase Remote Config: Using cached config');
    }
  } catch (error) {
    console.error('Error initializing Remote Config:', error);
    // Falls back to default values
  }
}

/**
 * Check if a feature flag is enabled
 * @param flag - Feature flag name
 * @returns boolean indicating if the flag is enabled
 */
export function isFeatureEnabled(flag: string): boolean {
  try {
    return getBoolean(remoteConfig, flag);
  } catch (error) {
    console.error(`Error reading flag ${flag}:`, error);
    // Fallback to default value
    const defaultValue = remoteConfig.defaultConfig[flag];
    return typeof defaultValue === 'boolean' ? defaultValue : false;
  }
}

/**
 * Get the value of a feature flag
 * @param flag - Feature flag name
 * @returns The value of the flag (string)
 */
export function getFeatureValue<T = string>(flag: string): T {
  try {
    const value = getValue(remoteConfig, flag);
    return value.asString() as T;
  } catch (error) {
    console.error(`Error reading value of flag ${flag}:`, error);
    return remoteConfig.defaultConfig[flag] as T;
  }
}

/**
 * React hook to use feature flags
 * Provides reactive updates when flags change
 *
 * @param flag - Feature flag name
 * @returns boolean indicating if the flag is enabled
 *
 * @example
 * const isDarkModeEnabled = useFeatureFlag(FEATURE_FLAGS.DARK_MODE);
 */
export function useFeatureFlag(flag: string): boolean {
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flag));

  useEffect(() => {
    // Update state if flag changes
    const currentValue = isFeatureEnabled(flag);
    if (currentValue !== enabled) {
      setEnabled(currentValue);
    }

    // Note: Firebase Remote Config doesn't have a built-in listener for config updates
    // in the Web SDK. Updates happen on fetchAndActivate() calls.
    // For real-time updates, you would need to:
    // 1. Call fetchAndActivate periodically
    // 2. Use Firestore or another real-time mechanism
    // 3. Implement a custom polling mechanism

    // For now, we rely on the minimumFetchIntervalMillis setting
  }, [flag, enabled]);

  return enabled;
}

/**
 * React hook to get feature flag value
 * Provides reactive updates when values change
 *
 * @param flag - Feature flag name
 * @returns The value of the flag
 *
 * @example
 * const llmProvider = useFeatureValue<string>(FEATURE_FLAGS.LLM_PROVIDER);
 */
export function useFeatureValue<T = string>(flag: string): T {
  const [value, setValue] = useState(() => getFeatureValue<T>(flag));

  useEffect(() => {
    const currentValue = getFeatureValue<T>(flag);
    if (currentValue !== value) {
      setValue(currentValue);
    }
  }, [flag, value]);

  return value;
}

/**
 * Get all feature flags and their values
 * Useful for debugging and admin panels
 */
export function getAllFeatureFlags(): Record<string, boolean | string> {
  const flags: Record<string, boolean | string> = {};

  Object.values(FEATURE_FLAGS).forEach((flag) => {
    try {
      // Try to get as boolean first
      const boolValue = getBoolean(remoteConfig, flag);
      flags[flag] = boolValue;
    } catch {
      // If not boolean, get as string
      try {
        const stringValue = getValue(remoteConfig, flag).asString();
        flags[flag] = stringValue;
      } catch (error) {
        console.error(`Error reading flag ${flag}:`, error);
        flags[flag] = (remoteConfig.defaultConfig[flag] as string | boolean) || false;
      }
    }
  });

  return flags;
}

// Export the Remote Config instance for advanced use cases
export { remoteConfig };
